import "./AgileHistoricalTable.scss";

import { Button, Flex, Image, Text } from "@fluentui/react-northstar";
import { getFirstEmoji } from "@teams/cards";
import { NominalQuestion, TeamsRecipientGroupMember, TeamsSurvey } from "@teams/types";
import { ScheduleDTO, SurveyDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import WarningImage from "../../../../_shared/assets/warning-image.svg";
import { AlertError } from "../../../../_shared/components/AlertError";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { TeamsLoader } from "../../../../_shared/components/TeamsLoader";
import { FetchError } from "../../../../_shared/services/ChannelApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { startTaskDeleteConfirmation } from "../../../../_shared/utils/startTaskDeleteConfirmation";
import { getPageContext } from "../../../../_shared/utils/teamsHelpers";
import { useHistoricalSurveys } from "../../../actions/useHistoricalSurveys";

type AgileHistoricalTableParams = {
  series: ScheduleDTO;
  groupId?: string;
};

export const AgileHistoricalTable: React.FC<AgileHistoricalTableParams> = ({ series }) => {
  const { teamsContext } = useTeamsContext();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const {
    error,
    loadHistoricalSurveys,
    isHistoricalSurveysLoading,
    historicalSurveys,
    historicalSurveyTotal,
  } = useHistoricalSurveys();

  const loadHistoricalSurveysCallback = useCallback(() => {
    loadHistoricalSurveys(
      teamsContext,
      series.id,
      { includeResponses: true, pageIndex },
      getGroupIdFromSeries(series)
    );
  }, [loadHistoricalSurveys, teamsContext, series, pageIndex]);

  useEffect(() => loadHistoricalSurveysCallback(), [loadHistoricalSurveysCallback]);

  if (error) {
    console.log(error);
    if ((error as FetchError).statusCode === 422) {
      return (
        <Flex column hAlign="center">
          <Text
            content={`Oh Bummer! The owners subscription has ended.`}
            size="large"
            weight="bold"
            color="brand"
            as="div"
          />

          <Text content="Contact the sender to renew their subscription to access these results." />

          <Image src={WarningImage} className="trial-expired__warning-image" />
        </Flex>
      );
    }
    return <AlertError error={error} onClick={() => loadHistoricalSurveysCallback()} />;
  }

  historicalSurveys.sort((a, b) => {
    if (!b.closeAt || !a.closeAt) return 0;

    return b.closeAt.getTime() - a.closeAt.getTime();
  });

  const onDeleteInstance = (survey: TeamsSurvey) => {
    startTaskDeleteConfirmation(
      null,
      { id: survey.id },
      "Delete Instance",
      series.applicationType,
      (_err, _result) => {
        loadHistoricalSurveysCallback();
      }
    );
  };

  return (
    <div className="agile-historical-table">
      <TeamsLoader
        isLoading={isHistoricalSurveysLoading && pageIndex === 0}
        loadingMessage="Loading history"
      >
        <table className="agile-historical-table__table">
          <thead>
            <tr>
              <th className="agile-historical-table__header-cell">Send Date</th>
              <th className="agile-historical-table__header-cell">Standup Status</th>
              <th className="agile-historical-table__header-cell">Completions</th>
              <th className="agile-historical-table__header-cell">Team Status Responses</th>
            </tr>
          </thead>
          <tbody className={`agile-historical-table__body`}>
            {historicalSurveys.map(survey => {
              return (
                <tr key={survey.id} className="agile-historical-table__row">
                  <td className="agile-historical-table__cell agile-historical-table__date">
                    <Link
                      className="agile-historical-table__link"
                      to={`${getPageContext()}/content/polly/survey/${survey.id}`}
                    >
                      <Text weight="bold" content={moment(survey.closeAt).format("LL")} />
                    </Link>
                  </td>
                  <td className="agile-historical-table__cell">
                    <Text
                      weight="bold"
                      content={survey.state === "open" ? "✳️ Open" : "✴️ Closed"}
                    />
                  </td>
                  <td className="agile-historical-table__cell">
                    <Text weight="bold" content={survey.responseCount} />
                  </td>
                  <td className="agile-historical-table__cell">{getStatusResponseText(survey)}</td>

                  <td className="agile-historical-table__cell agile-historical-table__action-cell">
                    <span
                      onClick={() => onDeleteInstance(survey as TeamsSurvey)}
                      className="agile-historical-table__action"
                    >
                      <PollyIcon icon="trash-icon" />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {isHistoricalSurveysLoading && (
          <Flex className="agile-historical-table__load-more">
            <Button
              data-tname="load-more-history"
              icon={<PollyIcon icon="agile-load-table-icon" />}
              fluid
              disabled={true}
              content="Loading..."
            />
          </Flex>
        )}

        {!isHistoricalSurveysLoading && historicalSurveyTotal > historicalSurveys.length && (
          <Flex className="agile-historical-table__load-more">
            <Button
              data-tname="load-more-history"
              icon={<PollyIcon icon="agile-load-table-icon" />}
              fluid
              onClick={() => {
                setPageIndex(pageIndex + 1);
              }}
              content="Load more"
            />
          </Flex>
        )}
      </TeamsLoader>
    </div>
  );
};

const getStatusResponseText = (survey: SurveyDTO) => {
  const statusResponses: Record<string, number> = {};
  const finalQuestion = survey.questions[2] as NominalQuestion;
  if (!finalQuestion || finalQuestion.questionType !== "nominal") {
    return;
  }

  survey.responses?.forEach(response => {
    const finalQuestionResponse = response.responses.find(r => r.questionId === finalQuestion.id);
    if (!finalQuestionResponse) {
      return;
    }
    const finalQuestionChoice = finalQuestion.choices.find(
      choice => choice.id === finalQuestionResponse.value
    );
    if (!finalQuestionChoice) {
      return;
    }
    const statusEmoji = getFirstEmoji(finalQuestionChoice.value);
    if (!statusEmoji) {
      return;
    }

    if (!statusResponses[statusEmoji]) {
      statusResponses[statusEmoji] = 0;
    }

    statusResponses[statusEmoji] += 1;
  });

  const statusEmojis = Object.keys(statusResponses);
  statusEmojis.sort();

  return statusEmojis.map(statusResponse => {
    return (
      <span key={statusResponse} className="agile-historical-table__status">
        {statusResponse}
        {statusResponses[statusResponse]}
      </span>
    );
  });
};

const getGroupIdFromSeries = (series: ScheduleDTO): string | undefined => {
  if (series?.recipients.length > 0) {
    return (series?.recipients[0] as TeamsRecipientGroupMember).groupId;
  }
};
